export type UserRole =
  | 'super_admin'
  | 'org_admin'
  | 'campaign_manager'
  | 'content_creator'
  | 'analyst'
  | 'partner_user'
  | 'agency_operator'
  | 'external_collaborator';

export const PERMISSION_ACTIONS = [
  'agency.manage_assignments',
  'agent.cancel',
  'agent.configure',
  'agent.execute',
  'analytics.export',
  'analytics.set_budget',
  'analytics.view',
  'analytics.view_audit',
  'analytics.view_cost',
  'campaign.approve',
  'campaign.create',
  'campaign.delete',
  'campaign.edit',
  'campaign.manage_collaborators',
  'campaign.export',
  'campaign.run_workflow',
  'campaign.view',
  'content.approve',
  'content.edit',
  'content.generate',
  'content.view',
  'credit.adjust',
  'credit.manage_caps',
  'credit.purchase',
  'credit.view_balance',
  'credit.view_partner_usage',
  'credit.view_usage',
  'document.delete',
  'document.edit_metadata',
  'document.ingest',
  'document.upload',
  'document.view',
  'ecosystem.create_edit',
  'ecosystem.delete',
  'ecosystem.view',
  'emma.chat',
  'integration.deploy',
  'integration.manage',
  'integration.view',
  'internal.view_cogs',
  'knowledge_graph.modify',
  'knowledge_graph.view',
  'org.change_plan',
  'org.delete',
  'org.edit_settings',
  'org.manage_ai_config',
  'org.manage_api_keys',
  'org.view_billing',
  'partition.archive',
  'partition.create',
  'partition.edit',
  'partition.manage_users',
  'partition.view',
  'persona.create_edit',
  'persona.delete',
  'persona.view',
  'platform.database_ops',
  'platform.feature_flags',
  'platform.impersonate',
  'platform.manage_orgs',
  'platform.view_all_orgs',
  'project.create',
  'project.delete',
  'project.edit',
  'project.view',
  'subscription.manage',
  'subscription.view',
  'user.change_role',
  'user.invite',
  'user.remove',
  'user.view_list',
] as const;

export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

export interface AuthUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: UserRole;
  status: string;
  email_verified: boolean;
  organization_id: string;
  account_id?: string | null;
  active_membership_id?: string | null;
  active_organization_id?: string | null;
  partition_id: string | null;
  is_platform_user: boolean;
  assigned_partition_ids: string[] | null;
  collaborator_campaign_ids?: string[] | null;
  memberships?: Array<{
    membership_id: string;
    organization_id: string;
    user_id: string;
    role: UserRole;
    status: string;
    is_default: boolean;
    last_accessed_at?: string | null;
  }>;
  created_at?: string;
  updated_at?: string;
}

type RoleAwareUser = Pick<AuthUser, 'role' | 'is_platform_user'>;

export const ADMIN_ROLES: readonly UserRole[] = ['super_admin', 'org_admin'];
export const EDITOR_ROLES: readonly UserRole[] = [
  'super_admin',
  'org_admin',
  'campaign_manager',
  'content_creator',
  'partner_user',
  'agency_operator',
];

export const ROLE_CAPABILITIES: Record<UserRole, readonly PermissionAction[]> = {
  super_admin: [
    'user.view_list',
    'user.invite',
    'user.remove',
    'user.change_role',
    'org.edit_settings',
    'org.manage_api_keys',
    'org.manage_ai_config',
    'org.view_billing',
    'org.change_plan',
    'org.delete',
    'project.view',
    'project.create',
    'project.edit',
    'project.delete',
    'campaign.view',
    'campaign.create',
    'campaign.edit',
    'campaign.delete',
    'campaign.manage_collaborators',
    'campaign.run_workflow',
    'campaign.approve',
    'campaign.export',
    'document.view',
    'document.upload',
    'document.edit_metadata',
    'document.delete',
    'document.ingest',
    'knowledge_graph.view',
    'knowledge_graph.modify',
    'ecosystem.view',
    'ecosystem.create_edit',
    'ecosystem.delete',
    'content.view',
    'content.generate',
    'content.edit',
    'content.approve',
    'persona.view',
    'persona.create_edit',
    'persona.delete',
    'emma.chat',
    'agent.execute',
    'agent.cancel',
    'agent.configure',
    'analytics.view',
    'analytics.view_cost',
    'analytics.set_budget',
    'analytics.view_audit',
    'analytics.export',
    'integration.view',
    'integration.manage',
    'integration.deploy',
    'platform.view_all_orgs',
    'platform.manage_orgs',
    'platform.feature_flags',
    'platform.database_ops',
    'platform.impersonate',
    'partition.view',
    'partition.create',
    'partition.edit',
    'partition.archive',
    'partition.manage_users',
    'agency.manage_assignments',
    // Phase 2: Credit & billing
    'credit.view_balance',
    'credit.view_usage',
    'credit.view_partner_usage',
    'credit.purchase',
    'credit.adjust',
    'credit.manage_caps',
    'subscription.view',
    'subscription.manage',
    'internal.view_cogs',
  ],
  org_admin: [
    'user.view_list',
    'user.invite',
    'user.remove',
    'user.change_role',
    'org.edit_settings',
    'org.manage_api_keys',
    'org.manage_ai_config',
    'org.view_billing',
    'org.change_plan',
    'org.delete',
    'project.view',
    'project.create',
    'project.edit',
    'project.delete',
    'campaign.view',
    'campaign.create',
    'campaign.edit',
    'campaign.delete',
    'campaign.manage_collaborators',
    'campaign.run_workflow',
    'campaign.approve',
    'campaign.export',
    'document.view',
    'document.upload',
    'document.edit_metadata',
    'document.delete',
    'document.ingest',
    'knowledge_graph.view',
    'knowledge_graph.modify',
    'ecosystem.view',
    'ecosystem.create_edit',
    'ecosystem.delete',
    'content.view',
    'content.generate',
    'content.edit',
    'content.approve',
    'persona.view',
    'persona.create_edit',
    'persona.delete',
    'emma.chat',
    'agent.execute',
    'agent.cancel',
    'agent.configure',
    'analytics.view',
    'analytics.view_cost',
    'analytics.set_budget',
    'analytics.view_audit',
    'analytics.export',
    'integration.view',
    'integration.manage',
    'integration.deploy',
    'partition.view',
    'partition.create',
    'partition.edit',
    'partition.archive',
    'partition.manage_users',
    'agency.manage_assignments',
    // Phase 2: Credit & billing (no adjust, no internal COGS)
    'credit.view_balance',
    'credit.view_usage',
    'credit.view_partner_usage',
    'credit.purchase',
    'credit.manage_caps',
    'subscription.view',
    'subscription.manage',
  ],
  campaign_manager: [
    'user.view_list',
    'project.view',
    'project.create',
    'project.edit',
    'campaign.view',
    'campaign.create',
    'campaign.edit',
    'campaign.delete',
    'campaign.manage_collaborators',
    'campaign.run_workflow',
    'campaign.approve',
    'campaign.export',
    'document.view',
    'document.upload',
    'document.edit_metadata',
    'document.delete',
    'document.ingest',
    'knowledge_graph.view',
    'knowledge_graph.modify',
    'ecosystem.view',
    'ecosystem.create_edit',
    'content.view',
    'content.generate',
    'content.edit',
    'content.approve',
    'persona.view',
    'persona.create_edit',
    'persona.delete',
    'emma.chat',
    'agent.execute',
    'agent.cancel',
    'analytics.view',
    'analytics.export',
    'integration.view',
    'integration.deploy',
  ],
  content_creator: [
    'project.view',
    'campaign.view',
    'campaign.edit',
    'campaign.run_workflow',
    'document.view',
    'document.upload',
    'document.edit_metadata',
    'document.ingest',
    'knowledge_graph.view',
    'ecosystem.view',
    'content.view',
    'content.generate',
    'content.edit',
    'persona.view',
    'persona.create_edit',
    'emma.chat',
    'agent.execute',
  ],
  analyst: [
    'project.view',
    'campaign.view',
    'document.view',
    'knowledge_graph.view',
    'ecosystem.view',
    'content.view',
    'persona.view',
    'emma.chat',
    'analytics.view',
    'analytics.view_cost',
    'analytics.view_audit',
    'analytics.export',
  ],
  partner_user: [
    'project.view',
    'project.create',
    'project.edit',
    'campaign.view',
    'campaign.create',
    'campaign.edit',
    'campaign.delete',
    'campaign.run_workflow',
    'campaign.approve',
    'campaign.export',
    'document.view',
    'document.upload',
    'document.edit_metadata',
    'document.delete',
    'document.ingest',
    'knowledge_graph.view',
    'ecosystem.view',
    'content.view',
    'content.generate',
    'content.edit',
    'content.approve',
    'persona.view',
    'persona.create_edit',
    'persona.delete',
    'emma.chat',
    'agent.execute',
    'agent.cancel',
    'analytics.view',
    'analytics.export',
    'integration.deploy',
    'credit.view_partner_usage',
  ],
  agency_operator: [
    'project.view',
    'project.create',
    'project.edit',
    'campaign.view',
    'campaign.create',
    'campaign.edit',
    'campaign.delete',
    'campaign.run_workflow',
    'campaign.approve',
    'campaign.export',
    'document.view',
    'document.upload',
    'document.edit_metadata',
    'document.ingest',
    'knowledge_graph.view',
    'ecosystem.view',
    'content.view',
    'content.generate',
    'content.edit',
    'content.approve',
    'persona.view',
    'persona.create_edit',
    'emma.chat',
    'agent.execute',
    'agent.cancel',
    'analytics.view',
    'analytics.export',
    'integration.deploy',
    'credit.view_partner_usage',
  ],
  external_collaborator: [
    'campaign.view',
    'document.view',
    'document.upload',
    'content.view',
  ],
};

export const PERMISSIONS: Record<PermissionAction, UserRole[]> = Object.entries(
  ROLE_CAPABILITIES,
).reduce((permissionMap, [role, actions]) => {
  actions.forEach((action) => {
    if (!permissionMap[action]) {
      permissionMap[action] = [];
    }
    permissionMap[action].push(role as UserRole);
  });
  return permissionMap;
}, {} as Record<PermissionAction, UserRole[]>);

export function hasPermission(role: UserRole, action: PermissionAction): boolean {
  const allowedRoles = PERMISSIONS[action];
  return Boolean(allowedRoles && allowedRoles.includes(role));
}

export function hasAnyRole(
  role: UserRole | null | undefined,
  allowedRoles: readonly UserRole[],
): boolean {
  return Boolean(role && allowedRoles.includes(role));
}

export function isAdminRole(role: UserRole | null | undefined): boolean {
  return hasAnyRole(role, ADMIN_ROLES);
}

export function isEditorRole(role: UserRole | null | undefined): boolean {
  return hasAnyRole(role, EDITOR_ROLES);
}

export function isAdminUser(user: RoleAwareUser | null | undefined): boolean {
  return Boolean(user) && (isAdminRole(user.role) || Boolean(user.is_platform_user));
}

